import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    getRoomState,
    joinRoom,
    revealClue,
    showClue,
    openBuzzer,
    buzzIn,
    judgeAnswer,
    closeClue,
    cancelClue,
    nextRound,
    setScore,
    finishGame,
    hostStorageKey,
    playerStorageKey,
} from "./api";
import "./jeopardy.css";

function loadPlayerInfo(code) {
    try {
        return JSON.parse(localStorage.getItem(playerStorageKey(code)) || "null");
    } catch {
        return null;
    }
}

export default function JeopardyRoom() {
    const { code } = useParams();
    const [state, setState] = useState(null);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const stateRef = useRef(null);

    const hostToken = localStorage.getItem(hostStorageKey(code));
    const [playerInfo, setPlayerInfo] = useState(() => loadPlayerInfo(code));

    useEffect(() => {
        let cancelled = false;
        let timeoutId;

        async function poll() {
            try {
                const res = await getRoomState(code);
                if (cancelled) return;
                if (res.error) {
                    setError(res.error);
                } else {
                    setState(res.state);
                    stateRef.current = res.state;
                    setError(null);
                }
            } catch {
                if (!cancelled) setError("Could not reach the game server.");
            }
            if (cancelled) return;
            const delay = stateRef.current && stateRef.current.status === "open" ? 400 : 1200;
            timeoutId = setTimeout(poll, delay);
        }

        poll();
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [code]);

    const applyResult = useCallback((res) => {
        if (res.error) {
            setActionError(res.error);
        } else {
            setActionError(null);
        }
        if (res.state) {
            setState(res.state);
            stateRef.current = res.state;
        }
    }, []);

    async function handleJoinSubmit(name) {
        const res = await joinRoom(code, name);
        if (res.error) {
            setActionError(res.error);
            return;
        }
        const info = { playerId: res.playerId, token: res.token, name };
        localStorage.setItem(playerStorageKey(code), JSON.stringify(info));
        setPlayerInfo(info);
        applyResult(res);
    }

    async function handleNextRound() {
        const res = await nextRound(code, hostToken);
        applyResult(res);
    }

    async function handleFinishGame() {
        if (!window.confirm("End the game for everyone? This can't be undone.")) return;
        const res = await finishGame(code, hostToken);
        applyResult(res);
    }

    if (error) {
        return (
            <div className="jpd-home">
                <div className="jpd-error">{error}</div>
                <Link className="jpd-btn" to="/jeopardy">
                    Back
                </Link>
            </div>
        );
    }

    if (!state) {
        return <div className="jpd-home">Loading game…</div>;
    }

    const isHost = Boolean(hostToken);
    const isPlayer = Boolean(playerInfo);
    const round = state.rounds[state.round];
    const hasNextRound = Boolean(state.rounds[state.round + 1]);

    return (
        <div className="jpd-room">
            <div className="jpd-room-header">
                <div className="jpd-room-code">
                    Room <strong>{code}</strong>
                    <CopyCodeButton code={code} />
                    {isHost && <span className="jpd-role-badge">HOST</span>}
                    {state.status !== "finished" && (
                        <span className="jpd-round-label">
                            {round.name} ({state.round + 1}/{state.rounds.length})
                        </span>
                    )}
                </div>
                <Scoreboard
                    state={state}
                    me={playerInfo}
                    isHost={isHost}
                    code={code}
                    hostToken={hostToken}
                    onResult={applyResult}
                />
                {isHost && state.status !== "finished" && (
                    <div className="jpd-host-actions">
                        {hasNextRound && state.status === "board" && (
                            <button className="jpd-btn" onClick={handleNextRound}>
                                Next Board
                            </button>
                        )}
                        <button className="jpd-btn jpd-btn-danger" onClick={handleFinishGame}>
                            End Game
                        </button>
                    </div>
                )}
            </div>

            {actionError && <div className="jpd-error">{actionError}</div>}

            {state.status === "finished" ? (
                <GameOver state={state} />
            ) : state.active ? (
                <ActiveClue
                    code={code}
                    state={state}
                    round={round}
                    isHost={isHost}
                    playerInfo={playerInfo}
                    onResult={applyResult}
                />
            ) : (
                <Board code={code} state={state} round={round} isHost={isHost} onResult={applyResult} />
            )}

            {!isHost && !isPlayer && state.status !== "finished" && <JoinInline onJoin={handleJoinSubmit} />}
        </div>
    );
}

function CopyCodeButton({ code }) {
    const [copied, setCopied] = useState(false);

    async function handleCopy() {
        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(code);
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = code;
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard access denied or unavailable - nothing more we can do.
        }
    }

    return (
        <button className="jpd-btn jpd-btn-small jpd-copy-btn" onClick={handleCopy} title="Copy room code">
            {copied ? "Copied!" : "Copy"}
        </button>
    );
}

function GameOver({ state }) {
    const ranked = [...state.players].sort((a, b) => b.score - a.score);
    return (
        <div className="jpd-gameover">
            <h2>Game Over</h2>
            {ranked.length === 0 && <div className="jpd-muted">No players joined this game.</div>}
            {ranked.map((p, i) => (
                <div key={p.id} className={"jpd-gameover-row" + (i === 0 ? " jpd-gameover-winner" : "")}>
                    <span>{i === 0 ? "🏆 " : ""}{p.name}</span>
                    <span>{p.score}</span>
                </div>
            ))}
            <div style={{ marginTop: 20 }}>
                <Link className="jpd-btn jpd-btn-primary" to="/jeopardy">
                    Back to Main Menu
                </Link>
            </div>
        </div>
    );
}

function Scoreboard({ state, me, isHost, code, hostToken, onResult }) {
    return (
        <div className="jpd-scoreboard">
            {state.players.length === 0 && <span className="jpd-muted">No players yet</span>}
            {state.players.map((p) => (
                <ScoreChip
                    key={p.id}
                    player={p}
                    isActive={state.currentBuzzer === p.id}
                    isMe={Boolean(me && me.playerId === p.id)}
                    isHost={isHost}
                    code={code}
                    hostToken={hostToken}
                    onResult={onResult}
                />
            ))}
        </div>
    );
}

function ScoreChip({ player, isActive, isMe, isHost, code, hostToken, onResult }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(player.score);

    async function handleSave(e) {
        e.preventDefault();
        const res = await setScore(code, hostToken, player.id, parseInt(value, 10) || 0);
        onResult(res);
        setEditing(false);
    }

    const className =
        "jpd-score-chip" + (isActive ? " jpd-score-chip-active" : "") + (isMe ? " jpd-score-chip-me" : "");

    if (editing) {
        return (
            <form className={className} onSubmit={handleSave}>
                <span className="jpd-score-name">{player.name}</span>
                <input
                    className="jpd-score-edit"
                    type="number"
                    value={value}
                    autoFocus
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleSave}
                />
            </form>
        );
    }

    return (
        <div
            className={className + (isHost ? " jpd-score-chip-editable" : "")}
            onClick={() => {
                if (isHost) {
                    setValue(player.score);
                    setEditing(true);
                }
            }}
            title={isHost ? "Click to edit score" : undefined}
        >
            <span className="jpd-score-name">{player.name}</span>
            <span className="jpd-score-value">{player.score}</span>
        </div>
    );
}

function Board({ code, round, isHost, onResult }) {
    const { categories, clues } = round;
    return (
        <div className="jpd-board">
            <div className="jpd-board-grid" style={{ gridTemplateColumns: `repeat(${categories.length}, 1fr)` }}>
                {categories.map((cat) => (
                    <div className="jpd-cat-header" key={cat}>
                        {cat}
                    </div>
                ))}
                {clues[0].map((_, rowIndex) =>
                    categories.map((cat, catIndex) => {
                        const clue = clues[catIndex][rowIndex];
                        return (
                            <BoardCell
                                key={`${catIndex}-${rowIndex}`}
                                code={code}
                                clue={clue}
                                category={catIndex}
                                row={rowIndex}
                                isHost={isHost}
                                onResult={onResult}
                            />
                        );
                    })
                )}
            </div>
        </div>
    );
}

function BoardCell({ code, clue, category, row, isHost, onResult }) {
    const hostToken = localStorage.getItem(hostStorageKey(code));

    async function handleClick() {
        if (!isHost || clue.answered) return;
        const res = await revealClue(code, hostToken, category, row);
        onResult(res);
    }

    if (clue.answered) {
        return <div className="jpd-cell jpd-cell-answered">{clue.wonBy ? "✓" : "—"}</div>;
    }

    return (
        <div className={"jpd-cell" + (isHost ? " jpd-cell-clickable" : "")} onClick={handleClick}>
            {clue.value}
        </div>
    );
}

function ActiveClue({ code, state, round, isHost, playerInfo, onResult }) {
    const { category, row } = state.active;
    const clue = round.clues[category][row];
    const hostToken = localStorage.getItem(hostStorageKey(code));
    const buzzedPlayer = state.players.find((p) => p.id === state.currentBuzzer);
    const isMe = playerInfo && state.currentBuzzer === playerInfo.playerId;
    const iAmLockedOut = playerInfo && state.lockedOut.includes(playerInfo.playerId);
    const contentVisible = isHost || state.status !== "revealed";

    async function act(fn) {
        const res = await fn();
        onResult(res);
    }

    return (
        <div className="jpd-clue">
            <div className="jpd-clue-value">{round.categories[category]} — {clue.value}</div>
            <div className="jpd-clue-prompt">
                {contentVisible ? (
                    clue.type === "image" ? (
                        <img src={clue.prompt} alt="clue" className="jpd-clue-image" />
                    ) : (
                        clue.prompt
                    )
                ) : (
                    <span className="jpd-muted">Waiting for the host to reveal this clue…</span>
                )}
            </div>

            {isHost && (
                <div className="jpd-clue-answer">Answer: {clue.answer}</div>
            )}

            {isHost && state.status === "revealed" && (
                <div className="jpd-clue-actions">
                    <span className="jpd-muted">Only you can see this clue.</span>
                    <button className="jpd-btn jpd-btn-primary" onClick={() => act(() => showClue(code, hostToken))}>
                        Show to Everyone
                    </button>
                    <button className="jpd-btn" onClick={() => act(() => cancelClue(code, hostToken))}>
                        Back (opened by accident)
                    </button>
                </div>
            )}

            {isHost && state.status === "shown" && (
                <div className="jpd-clue-actions">
                    <button className="jpd-btn jpd-btn-primary" onClick={() => act(() => openBuzzer(code, hostToken))}>
                        Open Buzzer
                    </button>
                    <button className="jpd-btn" onClick={() => act(() => closeClue(code, hostToken))}>
                        Close (no answer)
                    </button>
                    <button className="jpd-btn" onClick={() => act(() => cancelClue(code, hostToken))}>
                        Back (opened by accident)
                    </button>
                </div>
            )}

            {isHost && state.status === "open" && (
                <div className="jpd-clue-actions">
                    <span className="jpd-muted">Waiting for a buzz…</span>
                    <button className="jpd-btn" onClick={() => act(() => closeClue(code, hostToken))}>
                        Close (no answer)
                    </button>
                    <button className="jpd-btn" onClick={() => act(() => cancelClue(code, hostToken))}>
                        Back (opened by accident)
                    </button>
                </div>
            )}

            {isHost && state.status === "locked" && (
                <div className="jpd-clue-actions">
                    <span className="jpd-buzzed-name">{buzzedPlayer ? buzzedPlayer.name : "Someone"} buzzed in!</span>
                    <button className="jpd-btn jpd-btn-correct" onClick={() => act(() => judgeAnswer(code, hostToken, true))}>
                        Correct
                    </button>
                    <button className="jpd-btn jpd-btn-wrong" onClick={() => act(() => judgeAnswer(code, hostToken, false))}>
                        Wrong
                    </button>
                </div>
            )}

            {!isHost && playerInfo && (
                <div className="jpd-clue-actions">
                    {(state.status === "shown" || state.status === "open") && !iAmLockedOut && (
                        <PlayerBuzzer code={code} state={state} playerInfo={playerInfo} onResult={onResult} />
                    )}
                    {(state.status === "shown" || state.status === "open") && iAmLockedOut && (
                        <span className="jpd-muted">You already tried this clue</span>
                    )}
                    {state.status === "locked" && (
                        <span className="jpd-buzzed-name">
                            {isMe ? "You buzzed in!" : `${buzzedPlayer ? buzzedPlayer.name : "Someone"} buzzed in!`}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

const BUZZ_FEEDBACK = {
    early: "Too early! Wait for the light.",
    locked_out: "Locked out - you buzzed too early.",
    already_tried: "You already tried this clue.",
    too_late: "Someone else buzzed in first.",
};

function PlayerBuzzer({ code, state, playerInfo, onResult }) {
    const [message, setMessage] = useState(null);
    const isOpen = state.status === "open";

    useEffect(() => {
        if (!message) return undefined;
        const timer = setTimeout(() => setMessage(null), 2000);
        return () => clearTimeout(timer);
    }, [message]);

    async function handleBuzz() {
        const res = await buzzIn(code, playerInfo.token);
        onResult(res);
        if (res.buzzedIn) {
            setMessage(null);
        } else {
            setMessage(BUZZ_FEEDBACK[res.reason] || null);
        }
    }

    return (
        <div className="jpd-buzzer-area">
            <div className={"jpd-buzzer-light" + (isOpen ? " jpd-buzzer-light-on" : "")} />
            <button className="jpd-btn jpd-btn-buzz" onClick={handleBuzz}>
                BUZZ
            </button>
            {message && <div className="jpd-buzzer-message">{message}</div>}
        </div>
    );
}

function JoinInline({ onJoin }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        setBusy(true);
        await onJoin(trimmed);
        setBusy(false);
    }

    return (
        <form className="jpd-join-inline" onSubmit={handleSubmit}>
            <input
                className="jpd-input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
            />
            <button className="jpd-btn jpd-btn-primary" type="submit" disabled={busy}>
                Join this game
            </button>
        </form>
    );
}
